import * as Tabs from '@radix-ui/react-tabs'
import './styles.scss'
import { TicketForm, TicketFormProps } from '@Components/ticket-form/ticket-form'
import { TicketListMode } from '@Components/ticket-list/ticket-list'
import { useSelector } from 'react-redux'
import { RootState, useAppDispatch } from '@AppState/redux-store'
import { AddOnSelection, TicketInfo, TicketInfoErrors } from '@Config/ticket-form-types'
import { Utils } from '@Util/utils'
import { useCallback, useState } from 'react'
import { fetchTicketResults, NAV_VALUES, TicketModeActions } from '@AppState/ticket-info'
import Analytics, { AnalyticEvents } from '@Util/analytics'
import { AddOnTypes } from '@Config/types'
import { saveTicket } from '@AppState/ticket-history'
import { TicketWatchMode } from '@Components/watch-animation/ticket-mode-animation'
import { BetExclusionType } from '@interaction-gaming/game-data-service-browser-client'
const CLASS_NAME = 'gby-k2g-ticket-mode'

export const TicketMode = () => {
  const gameConfig = useSelector((state: RootState) => state.gameConfig.gameRules)
  const latestDraw = useSelector((state: RootState) => state.latestDraw.latestDraw)
  const ticketModeData = useSelector((state: RootState) => state.ticketMode)

  const [drawIndex, setDrawIndex] = useState<number>(0)

  const dispatch = useAppDispatch()

  const defaultTicketInfo = useCallback(() => {
    const defaultStartNumber = latestDraw ?? 1
    const defaultGameConfig = gameConfig?.at(0) ?? null

    const defaultNumberOfDraws = defaultGameConfig
      ? Utils.mapRangeWithArrayToArray(defaultGameConfig.supportedDrawRanges)[0]
      : 1

    const defaultBaseWager = defaultGameConfig ? defaultGameConfig.baseWagerOptionsInCents[0] : 1

    const defaultTicketInfo: TicketInfo = {
      startingDrawNumber: defaultStartNumber,
      numberOfDraws: defaultNumberOfDraws,
      baseWager: defaultBaseWager,
      numberSelections: [],
      multiplierAddOnSelection: undefined,
      miniGameAddOnSelection: undefined,
      extraDrawAddOnSelection: undefined,
    }

    return defaultTicketInfo
  }, [gameConfig, latestDraw])

  const initialTicketInfo = useCallback(() => {
    return ticketModeData.activeTicket ?? defaultTicketInfo()
  }, [ticketModeData, defaultTicketInfo])

  const [ticketFormInfo, setTicketFormInfo] = useState<TicketInfo>(initialTicketInfo)

  const reset = useCallback(() => {
    const defaultTicket = defaultTicketInfo()
    setTicketFormInfo(defaultTicket)
    setDrawIndex(0)
  }, [defaultTicketInfo])

  const minDrawNumber = gameConfig?.at(0)?.activeDrawNumberRange.min || 1
  const maxDrawNumber =
    gameConfig?.at(-1)?.activeDrawNumberRange.max ?? (latestDraw ? latestDraw + 5 : 100000000)

  const ticketFormProps = useCallback(() => {
    const props: TicketFormProps = {
      minDrawNumber,
      maxDrawNumber,
      firstDrawNumberControl: {
        value: ticketFormInfo.startingDrawNumber === 0 ? null : ticketFormInfo.startingDrawNumber,
        onChange: (val: number | null) => {
          setTicketFormInfo({
            ...ticketFormInfo,
            startingDrawNumber: val ? val : 0,
          })
          dispatch(TicketModeActions.resetFetchState())
        },
      },
      numberOfDrawsControl: {
        value: ticketFormInfo.numberOfDraws,
        onChange: (val: number) => {
          setTicketFormInfo({
            ...ticketFormInfo,
            numberOfDraws: val,
          })
          dispatch(TicketModeActions.resetFetchState())
        },
      },
      costPerDrawControl: {
        value: ticketFormInfo.baseWager,
        onChange: (val: number) => {
          setTicketFormInfo({
            ...ticketFormInfo,
            baseWager: val,
          })
          dispatch(TicketModeActions.resetFetchState())
        },
      },
      numberSelectionsControl: {
        value: ticketFormInfo.numberSelections,
        onChange: (val: number[]) => {
          setTicketFormInfo({
            ...ticketFormInfo,
            numberSelections: [...val].sort((a, b) => a - b),
          })
          dispatch(TicketModeActions.resetFetchState())
        },
      },
      addOnSelectionControl: {
        value: [
          ticketFormInfo.extraDrawAddOnSelection,
          ticketFormInfo.multiplierAddOnSelection,
          ticketFormInfo.miniGameAddOnSelection,
        ].filter((val) => val !== undefined),
        onChange: (val: AddOnSelection[]) => {
          setTicketFormInfo({
            ...ticketFormInfo,
            extraDrawAddOnSelection: val.find((sel) => sel.type === AddOnTypes.EXTRA_DRAWS),
            miniGameAddOnSelection: val.find((sel) => sel.type === AddOnTypes.MINI_GAME),
            multiplierAddOnSelection: val.find((sel) => sel.type === AddOnTypes.MULTIPLIER),
          })
          dispatch(TicketModeActions.resetFetchState())
        },
      },
    }
    return props
  }, [dispatch, ticketFormInfo, minDrawNumber, maxDrawNumber])

  const validStartingDrawNumber = useCallback(() => {
    const val = ticketFormProps().firstDrawNumberControl.value

    if (!val) return false
    if (minDrawNumber && maxDrawNumber && val && (val < minDrawNumber || val > maxDrawNumber)) {
      return false
    }
    return true
  }, [minDrawNumber, maxDrawNumber, ticketFormProps])

  const validNumberSelections = useCallback(() => {
    const defaultGameConfig = gameConfig?.at(0)
    if (defaultGameConfig && defaultGameConfig.baseWagerRestrictions) {
      const wagerRestriction = defaultGameConfig.baseWagerRestrictions.find(
        (val) => val.wagerAmountInCents === ticketFormProps().costPerDrawControl.value,
      )

      if (wagerRestriction) {
        const rule = wagerRestriction.exclusionRules.find(
          (val) => val.type === BetExclusionType.SPOTS,
        )
        if (rule) {
          const numSelections = ticketFormProps().numberSelectionsControl.value.length
          return numSelections < Math.min(...rule.values) && numSelections != 0
        }
      }
    }
    return ticketFormProps().numberSelectionsControl.value.length > 0
  }, [ticketFormProps, gameConfig])

  const getFormErrors: () => TicketInfoErrors = useCallback(() => {
    return {
      startingDrawNumberError: !validStartingDrawNumber(),
      numberSelectionsError: !validNumberSelections(),
    }
  }, [validStartingDrawNumber, validNumberSelections])

  const validForm = useCallback(() => {
    return Object.keys(getFormErrors()).every((k) => !getFormErrors()[k])
  }, [getFormErrors])

  const onButtonReset = () => {
    dispatch(TicketModeActions.updateActiveMenu(NAV_VALUES.EDIT))
    reset()
  }

  const newTicketButtonHandler = () => {
    dispatch(TicketModeActions.updateActiveMenu(NAV_VALUES.EDIT))
    dispatch(TicketModeActions.updateTicketForm(defaultTicketInfo()))
    reset()
  }
  const editTicketButtonHandler = () => {
    dispatch(TicketModeActions.updateActiveMenu(NAV_VALUES.EDIT))
    setDrawIndex(0)
  }
  const watchTicketButtonHandler = () => {
    if (ticketModeData.activeMenu === NAV_VALUES.WATCH_TICKET) {
      return
    }
    dispatch(TicketModeActions.updateActiveMenu(NAV_VALUES.WATCH_TICKET))

    Analytics.trackPageView(AnalyticEvents.WATCH_TICKET)
    Analytics.track(AnalyticEvents.WATCH_TICKET, ticketFormInfo)

    dispatch(TicketModeActions.updateTicketForm(ticketFormInfo))
    dispatch(fetchTicketResults(ticketFormInfo))
    dispatch(saveTicket(ticketFormInfo))
  }
  const onButtonList = () => {
    if (ticketModeData.activeMenu === NAV_VALUES.LIST_TICKET) {
      return
    }
    dispatch(TicketModeActions.updateActiveMenu(NAV_VALUES.LIST_TICKET))

    Analytics.trackPageView(AnalyticEvents.LIST_TICKET)
    Analytics.track(AnalyticEvents.LIST_TICKET, ticketFormInfo)

    dispatch(TicketModeActions.updateTicketForm(ticketFormInfo))
    dispatch(saveTicket(ticketFormInfo))
  }

  const inTicketForm = ticketModeData.activeMenu === NAV_VALUES.EDIT

  return (
    <div className={`${CLASS_NAME}`}>
      <Tabs.Root
        className={`${CLASS_NAME}-navbar`}
        value={ticketModeData.activeMenu}
        activationMode="manual">
        <Tabs.Content className={`${CLASS_NAME}__content`} value={NAV_VALUES.EDIT}>
          <div className={`${CLASS_NAME}__content-title`} role={'heading'} aria-level={1}>
            Check Ticket
          </div>
          <div className={`${CLASS_NAME}__content-body`}>
            <TicketForm {...ticketFormProps()} errors={getFormErrors()} />
          </div>
        </Tabs.Content>
        <Tabs.Content className={`${CLASS_NAME}__content`} value={NAV_VALUES.WATCH_TICKET}>
          <div className={`${CLASS_NAME}__content-body`}>
            <TicketWatchMode
              drawIndex={drawIndex}
              onDrawingIndexChange={(i) => setDrawIndex(i)}
              onNewTicketClick={newTicketButtonHandler}
            />
          </div>
        </Tabs.Content>
        <Tabs.Content className={`${CLASS_NAME}__content`} value={NAV_VALUES.LIST_TICKET}>
          <div className={`${CLASS_NAME}__content-title`}>List Ticket</div>
          <div className={`${CLASS_NAME}__content-body`}>
            <TicketListMode />
          </div>
        </Tabs.Content>
        <Tabs.List className={`${CLASS_NAME}-navbar__list`}>
          <div className={`${CLASS_NAME}-navbar__ticket-form`}>
            {inTicketForm ? (
              <Tabs.Trigger
                className={`${CLASS_NAME}-navbar__item`}
                onClick={onButtonReset}
                tabIndex={0}
                value={NAV_VALUES.EDIT}>
                Reset
              </Tabs.Trigger>
            ) : null}
            {inTicketForm ? null : (
              <Tabs.Trigger
                className={`${CLASS_NAME}-navbar__item`}
                onClick={newTicketButtonHandler}
                tabIndex={0}
                value={NAV_VALUES.EDIT}>
                New
              </Tabs.Trigger>
            )}
            {inTicketForm ? null : (
              <Tabs.Trigger
                className={`${CLASS_NAME}-navbar__item`}
                onClick={editTicketButtonHandler}
                tabIndex={0}
                value={NAV_VALUES.EDIT}>
                Edit
              </Tabs.Trigger>
            )}
          </div>
          <div className={`${CLASS_NAME}-navbar__view-results`}>
            <Tabs.Trigger
              className={`${CLASS_NAME}-navbar__item`}
              onClick={watchTicketButtonHandler}
              value={NAV_VALUES.WATCH_TICKET}
              data-state={
                ticketModeData.activeMenu === NAV_VALUES.WATCH_TICKET ? 'active' : 'inactive'
              }
              tabIndex={validForm() ? 0 : -1}
              disabled={!validForm()}>
              Watch Ticket
            </Tabs.Trigger>
            <Tabs.Trigger
              className={`${CLASS_NAME}-navbar__item`}
              onClick={onButtonList}
              value={NAV_VALUES.LIST_TICKET}
              data-state={
                ticketModeData.activeMenu === NAV_VALUES.LIST_TICKET ? 'active' : 'inactive'
              }
              tabIndex={validForm() ? 0 : -1}
              disabled={!validForm()}>
              List Ticket
            </Tabs.Trigger>
          </div>
        </Tabs.List>
      </Tabs.Root>
    </div>
  )
}
